import { IconGraphy, Table, Tooltip, Typography } from '@leapeasy/ui-kit';
import { Badge } from '@leapeasy/ui-kit';
import { useDispatch } from 'react-redux';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { stageLabelForDisplay } from 'utils/residentTerminology';
import { Box, Dialog } from '@mui/material';
import styled from 'styled-components';
import { setDocTitleAction, setDocFileAction } from 'store/actions/documentActions';
import { Layerbar } from 'components/common/Layerbar';
import { DocuList } from 'components/DocuList';
import ClaimForm from 'containers/ClaimForm';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import { APPLICATION_STAGE_TABLE_META } from 'data/constants/common_constants';
import { getProgramAppTypeTableMeta } from 'utils/programAppTypeDisplay';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  APPLICATION_TABLE_DEFAULT_VISIBLE_KEYS,
  APPLICATION_TABLE_DEFAULT_VISIBLE_KEYS_WITHOUT_BUILDING,
  APPLICATION_TABLE_STORAGE_KEY,
} from './applicationTableConfig';
import { formatBooleanYesNo } from 'utils/formatBooleanYesNo';

const LEAP_PURPLE = '#702572';
const DISABLED_ICON_COLOR = '#B8B0BC';
const POLICY_ISSUED_STAGE = '05 - Policy Issued';

const formatTableDate = (value) => {
  if (value == null || value === '') return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const buildApplicationTableRow = (app, isDemo) => ({
  apartment_building_name: isDemo ? getDemoData('building-name') : app.apartment_building_name,
  rider_id: isDemo ? getDemoData('rider-id') : app.rider_id,
  stage: app.stage,
  tenant_1_name: isDemo ? getDemoData('tenant-name') : app.tenant_1_name,
  sf_createdDate: formatTableDate(app.sf_createdDate),
  lease_start_date: app.lease_start_date,
  app_type: app.app_type,
  gross_monthly_rent: app.gross_monthly_rent ? `$${parseFloat(app.gross_monthly_rent)}` : '$0.00',
  risk_class: app.risk_class || '',
  lease_end_date: app.lease_end_date,
  active_lease: app.active_lease,
  total_number_of_tenants: app.total_number_of_tenants,
  actions: app.id,
  id: app.id,
});

const dateColumnOptions = (overrides = {}) => ({
  fitContent: true,
  minWidth: 108,
  maxWidth: 116,
  shrink: false,
  ...overrides,
});

export const ApplicationTable = ({
  applications,
  filter,
  dropFilter,
  onChangeFilter,
  pagination,
  onChangePagination,
  sortOptions,
  onChangeSort,
  onClickApplication,
  shouldShowBuildingName = true,
  onRequestExportData,
  onRequestFullExportData,
  exportFullDatasetTotal,
  onLoadFullDatasetTotal,
  isLoadingFullDatasetTotal,
  listFilterActiveSummary,
  listFilterActiveCount,
  loading = false,
}) => {
  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const dispatch = useDispatch();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [docModal, setDocModal] = useState(null);
  const tableRef = useRef(null);

  const tableData = useMemo(() => {
    if (!applications) return [];
    return applications.map((app) => buildApplicationTableRow(app, isDemo));
  }, [applications, isDemo]);

  const resolveApplicationFromTableRow = useCallback(
    (row) => {
      if (!row || !applications?.length) return null;
      const appId = typeof row === 'object' && !Array.isArray(row) ? row.id : row[row.length - 1];
      return applications.find((app) => app.id === appId) ?? null;
    },
    [applications],
  );

  const goDetailPage = useCallback(
    (row) => {
      const selectedApplication = resolveApplicationFromTableRow(row);
      if (selectedApplication) {
        onClickApplication(selectedApplication);
      }
    },
    [onClickApplication, resolveApplicationFromTableRow],
  );

  const handleClaimData = useCallback(
    (row) => {
      const app = resolveApplicationFromTableRow(row);
      if (!app) return;
      setClaimData(app);
    },
    [resolveApplicationFromTableRow],
  );

  const handleDownloadInsurance = useCallback(
    (row) => {
      const app = resolveApplicationFromTableRow(row);
      if (!app) return;
      dispatch(setDocTitleAction(null));
      dispatch(setDocFileAction(null));
      setDocModal(app);
    },
    [dispatch, resolveApplicationFromTableRow],
  );

  const closeDocModal = useCallback(() => {
    dispatch(setDocTitleAction(null));
    dispatch(setDocFileAction(null));
    setDocModal(null);
  }, [dispatch]);

  const STAGE = APPLICATION_STAGE_TABLE_META;

  const getStageMeta = (value) =>
    STAGE.find((item) => item.value === value) || { label: value || 'Unknown', badge: 'neutral' };

  const defaultVisibleColumnKeys = useMemo(
    () =>
      shouldShowBuildingName
        ? APPLICATION_TABLE_DEFAULT_VISIBLE_KEYS
        : APPLICATION_TABLE_DEFAULT_VISIBLE_KEYS_WITHOUT_BUILDING,
    [shouldShowBuildingName],
  );

  const columns = useMemo(() => {
    const allColumns = [
      {
        name: 'Property Name',
        key: 'apartment_building_name',
        options: {
          minWidth: 160,
          flexGrow: 1,
          ellipsis: false,
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Policy ID',
        key: 'rider_id',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 128, maxWidth: 160 }),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Stage',
        key: 'stage',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 96, maxWidth: 140 }),
          sort: true,
          customBodyRenderer: (value) => {
            const stageMeta = getStageMeta(value);
            return (
              <Tooltip title={stageLabelForDisplay(value) || 'Unknown'} placement="top" showCarot>
                <div>
                  <Box textAlign="center">
                    <Badge
                      background="#F3F1F4"
                      color={stageMeta.badge}
                      label={stageLabelForDisplay(value) || stageMeta.label || 'Unknown'}
                      rounded
                      textSize="medium"
                    />
                  </Box>
                </div>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'Name',
        key: 'tenant_1_name',
        options: {
          ...(shouldShowBuildingName
            ? fitContentTextColumnOptions({ minWidth: 120, maxWidth: 280, flexGrow: 0 })
            : { minWidth: 140, flexGrow: 1, ellipsis: false }),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Create date',
        key: 'sf_createdDate',
        options: {
          ...dateColumnOptions(),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Lease Start Date',
        key: 'lease_start_date',
        options: {
          ...dateColumnOptions(),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Application Type',
        key: 'app_type',
        options: {
          ...fitContentCompactColumnOptions({ maxWidth: 152 }),
          sort: true,
          customBodyRenderer: (value) => {
            if (!value) return null;
            const appType = getProgramAppTypeTableMeta(value, 'application');
            return (
              <Tooltip title={appType.label} placement="top" showCarot>
                <div>
                  <Box textAlign="center">
                    <Badge
                      background="#F3F1F4"
                      color={appType.badge}
                      label={appType.label}
                      rounded
                      textSize="medium"
                    />
                  </Box>
                </div>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'Monthly Rent',
        key: 'gross_monthly_rent',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 120 }),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Risk Class',
        key: 'risk_class',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 120 }),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value || '-'}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Lease End Date',
        key: 'lease_end_date',
        options: {
          ...dateColumnOptions(),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Active',
        key: 'active_lease',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 96, maxWidth: 120 }),
          sort: true,
          exportValue: (value) => formatBooleanYesNo(value),
          customBodyRenderer: (value) => (
            <Tooltip title={formatBooleanYesNo(value)} placement="top" showCarot>
              <div>
                <Badge
                  background="#F3F1F4"
                  color={value === 'false' ? 'parisGreen' : 'purple'}
                  label={formatBooleanYesNo(value)}
                  rounded
                  textSize="medium"
                />
              </div>
            </Tooltip>
          ),
        },
      },
      {
        name: 'Residents',
        key: 'total_number_of_tenants',
        options: {
          ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 104, align: 'center' }),
          sort: true,
          customBodyRenderer: (value) => (
            <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
              {value}
            </CustomTypography>
          ),
        },
      },
      {
        name: 'Actions',
        key: 'actions',
        options: {
          width: 112,
          minWidth: 112,
          maxWidth: 112,
          shrink: false,
          align: 'center',
          sort: false,
          excludeFromExport: true,
          stickyRight: true,
          customBodyRenderer: (_value, row) => {
            const app = resolveApplicationFromTableRow(row);
            const stage = app?.stage;
            const policyActionsAvailable = stage === POLICY_ISSUED_STAGE;

            return (
              <ActionsCell onClick={(e) => e.stopPropagation()}>
                <ActionIconButton
                  icon="FileFolder.Description"
                  title="View Policy Details"
                  disabled={false}
                  onClick={() => goDetailPage(row)}
                />
                <ActionIconButton
                  icon="FileFolder.EditDocument"
                  title="File a claim"
                  disabled={!policyActionsAvailable}
                  onClick={() => handleClaimData(row)}
                />
                <ActionIconButton
                  icon="FileFolder.SimCardDownload"
                  title="Download Insurance"
                  disabled={!policyActionsAvailable}
                  onClick={() => handleDownloadInsurance(row)}
                />
              </ActionsCell>
            );
          },
        },
      },
    ];

    if (!shouldShowBuildingName) {
      return allColumns.filter((column) => column.key !== 'apartment_building_name');
    }

    return allColumns;
  }, [
    shouldShowBuildingName,
    goDetailPage,
    handleClaimData,
    handleDownloadInsurance,
    resolveApplicationFromTableRow,
  ]);

  const buildExportRow = useCallback(
    (app) => {
      const row = buildApplicationTableRow(app, isDemo);
      if (!shouldShowBuildingName) {
        const { apartment_building_name: _removed, ...rest } = row;
        return rest;
      }
      return row;
    },
    [isDemo, shouldShowBuildingName],
  );

  return (
    <TableContainer>
      <Dialog
        open={Boolean(docModal)}
        onClose={closeDocModal}
        PaperProps={{
          sx: {
            width: '640px',
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 64px)',
            margin: '16px',
            borderRadius: '12px',
            overflow: 'hidden',
          },
        }}
      >
        {docModal && (
          <DialogContainer>
            <DownloadInsuranceContainer>
              <div className="title">
                <div>
                  <p>Download Insurance</p>
                  <IconGraphy icon="General.Clear" onClick={closeDocModal} />
                </div>
                <p className="desc">Explore and download insurance files.</p>
              </div>
              <DocuList data={docModal.app_id} />
            </DownloadInsuranceContainer>
          </DialogContainer>
        )}
      </Dialog>

      {claimData && (
        <Layerbar>
          <ClaimForm data={{ ...claimData }} onClose={() => setClaimData(null)} />
        </Layerbar>
      )}

      <Table
        ref={tableRef}
        loading={loading}
        showEmptyState
        emptyContent={
          <GlobalUXState
            variant={filter?.searchText ? 'noResults' : 'noData'}
            compact
            description={
              filter?.searchText
                ? 'No applications match your search.'
                : 'No applications available yet.'
            }
          />
        }
        onExportButtonClick={() => setExportModalOpen(true)}
        autoTooltipForDefaultCells
        defaultVisibleColumnKeys={defaultVisibleColumnKeys}
        columnVisibilityStorageKey={APPLICATION_TABLE_STORAGE_KEY}
        columnWidthStorageKey={APPLICATION_TABLE_STORAGE_KEY}
        onRequestExportData={async () => {
          const exportRows = onRequestExportData ? await onRequestExportData() : applications;
          return (exportRows || []).map((app) => buildExportRow(app));
        }}
        onRequestFullExportData={
          onRequestFullExportData
            ? async () => {
                const exportRows = await onRequestFullExportData();
                return (exportRows || []).map((app) => buildExportRow(app));
              }
            : undefined
        }
        data={tableData}
        filter={filter}
        dropFilter={dropFilter}
        dropFilterActiveCount={listFilterActiveCount}
        dropFilterActiveSummary={listFilterActiveSummary}
        onChangeFilter={onChangeFilter}
        onChangeRowsPerPage={(value) =>
          onChangePagination({
            ...pagination,
            rowsPerPage: value,
            pageNumber: 0,
          })
        }
        pagination={pagination}
        sortOptions={sortOptions}
        onChangeSort={onChangeSort}
        style={{ width: '100%' }}
        title="Applications"
        columns={columns}
      />
      <ListExportScopeModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        tableRef={tableRef}
        title="Applications"
        currentPageRowCount={tableData.length}
        paginationTotalItems={pagination.totalItems}
        fullDatasetTotal={exportFullDatasetTotal}
        onLoadFullDatasetTotal={onLoadFullDatasetTotal}
        isLoadingFullDatasetTotal={isLoadingFullDatasetTotal}
        enableFiltered={Boolean(onRequestExportData)}
        enableFull={Boolean(onRequestFullExportData)}
        enableSelected={false}
      />
    </TableContainer>
  );
};

ApplicationTable.displayName = 'ApplicationTable';

const ActionIconButton = ({ icon, title, disabled, onClick }) => {
  const tooltip = disabled ? `${title} — Not available for this application` : title;

  return (
    <Tooltip title={tooltip} placement="top" showCarot>
      <span>
        <ActionIcon
          type="button"
          disabled={disabled}
          aria-label={title}
          onClick={disabled ? undefined : onClick}
        >
          <IconGraphy icon={icon} style={{ color: disabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }} />
        </ActionIcon>
      </span>
    </Tooltip>
  );
};

const TableContainer = styled.div``;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const ActionIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;

const DownloadInsuranceContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  * {
    box-sizing: border-box;
  }
  p {
    color: rgba(46, 15, 64, 1);
    margin: 0;
  }
  .title {
    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 0;
      font-size: 20px;
      font-weight: 500;
      svg {
        cursor: pointer;
      }
    }
    margin-bottom: 30px;
    border-bottom: 3px solid rgba(46, 15, 64, 0.5);
  }

  & > div:nth-of-type(2) {
    max-height: 373px;
    overflow: auto;
  }

  .desc {
    color: rgba(106, 94, 113, 1);
    font-weight: 400;
    font-size: 12px;
    margin-bottom: 10px;
  }
`;

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;

const DialogContainer = styled.div`
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  overflow: hidden;
  max-height: 500px;
  box-sizing: border-box;
  * {
    box-sizing: border-box;
  }
`;
